'use client'

import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import * as styles from './Sidebar.css'
import { isPageScrolledStore, isSidebarScrolledStore } from '../Clause/scroll-store'
import { useStore } from '@/lib/store/store'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import { TocContext, scrollHintStore } from './Toc'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip/Tooltip'
import { useSidebarScroll } from '@/components/Norm/useSidebarScroll'

import { type SidebarProps } from '@/components/Norm/NormLayout'
import Heading from '@/components/Heading/Heading'
import { Row, Stack } from '@christiankaindl/lyts'
import { MoreHorizontalIcon, SearchIcon } from 'lucide-react'
import Button from '@/components/Button/Button'
import { useSearchOverlay } from '@/app/[category]/[subCategory]/[norm]/SearchOverlay'
import LawIcon from '@/public/assets/law.icon.svg'
import { theme } from '@/styles/theme.css'
import Text from '@/components/Text/Text'
import { Menu, MenuButton } from '@/components/Menu'
import { useTooltipStore } from '@ariakit/react'
import SidebarContent from './Sidebar.server'

export default function ClientSidebar ({
  className,
  ...props
}: SidebarProps & { className?: string }) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  useSidebarScroll(scrollContainerRef)

  return (
    <ScrollArea.Root style={{ height: '100%', marginLeft: -21 }} type='auto' className={className}>
      <ScrollArea.Viewport
        ref={scrollContainerRef}
        className={styles.viewport}
        id='sidebar-desktop-scroll-container'
      >
        <TocContext.Provider value={{ ref: scrollContainerRef }}>
          <SidebarContent
            {...props}
          />
        </TocContext.Provider>
      </ScrollArea.Viewport>
      <CondScrollbar />
    </ScrollArea.Root>
  )
}

export function Header ({ forceShowTitle, title, className, children, abbreviation }: { forceShowTitle: boolean, title: string, children: ReactNode, className?: string, abbreviation?: string }) {
  const _pageScrolled = useStore(isPageScrolledStore)
  const _sidebarScrolled = useStore(isSidebarScrolledStore)

  const [pageScrolled, setPageScrolled] = useState(false)
  const [sidebarScrolled, setSidebarScrolled] = useState(false)
  const showTitle = forceShowTitle || pageScrolled

  useEffect(() => {
    setPageScrolled(_pageScrolled)
    setSidebarScrolled(_sidebarScrolled)
  }, [_pageScrolled, _sidebarScrolled])

  const showHeader = showTitle || sidebarScrolled

  const tooltip = useTooltipStore()

  function scrollToTop () {
    document.documentElement.scrollTo({ top: 0 })
    document.getElementById('sidebar-desktop-scroll-container')?.scrollTo({ top: 0 })
    tooltip.hide()
  }

  return (
    <Row asChild xAlign='space-between' gap={0.5}>
      <header className={className} style={{ contain: 'unset', transition: 'height 300ms' }}>
        <Heading weight={700} maxLines={1} h={5} as='h3' style={{ opacity: showHeader ? 1 : 0, transition: 'opacity 120ms', fontSize: '1.1em', wordBreak: 'break-all', flexShrink: 0, textWrap: 'balance', maxWidth: sidebarScrolled ? '80%' : '100%' }}>
          <Tooltip placement='bottom-start' store={tooltip}>
            <TooltipTrigger onClick={scrollToTop} disabled={!pageScrolled} render={<Row tabIndex={0} gap={0.25} style={{ color: theme.colors.text.brand, cursor: 'default' }} />}>
              <LawIcon width={16} />
              <Text as='span' color={theme.colors.text.brand} weight={600} maxLines={1}>{abbreviation ?? title}</Text>
            </TooltipTrigger>
            <TooltipContent render={<Stack gap='2px' />} style={{ textAlign: 'left' }} onClick={scrollToTop}>
              <b>{title}</b>
              <i>Zum Seitenanfang springen</i>
            </TooltipContent>
          </Tooltip>
        </Heading>
        <div style={{ visibility: sidebarScrolled ? undefined : 'hidden' }}>
          {children}
        </div>
        <Separator />
      </header>
    </Row>
  )
}
function Separator () {
  const isScrolled = useStore(isSidebarScrolledStore)
  return (
    <hr style={{ visibility: isScrolled ? 'visible' : 'hidden', position: 'absolute', bottom: 0, left: 21, width: 'calc(100% - 42px)' }} />
  )
}

export const ActionRow = memo(function ActionRow ({ menu, showSearch }: { menu: ReactNode, showSearch: boolean, headerButtons?: ReactNode, noToc?: boolean }) {
  const dialog = useSearchOverlay((state) => state.dialog)
  return (
    <Row gap={0.5} key='action-buttons' bleedLeft={10}>
      {showSearch && (
        <Button
          onClick={() => { dialog?.show() }}
          variant='flat'
          size='small'
          icon={SearchIcon}
          title='Durchsuchen'
        />
      )}
      <Menu>
        <MenuButton size='small' variant='flat' square title='Mehr'>
          <MoreHorizontalIcon size={20} />
        </MenuButton>
        {menu}
      </Menu>
    </Row>
  )
})

export function CondScrollbar () {
  const isScrolled = useStore(isPageScrolledStore)

  if (!isScrolled) return null

  return (
    <ScrollArea.Scrollbar
      className={styles.scrollbar}
    >
      <TocScrollHighlight />
      <ScrollArea.Thumb className={styles.thumb} />
    </ScrollArea.Scrollbar>
  )
}

export function TocScrollHighlight () {
  const showHint = useStore(scrollHintStore)

  return (
    <Tooltip placement='right'>
      <TooltipTrigger
        render={(
          <button
            style={{
              transform: `translateY(${showHint.top}px)`,
            }}
            className={styles.tocScrollHighlight}
            onClick={() => {
              showHint.scrollToElement()
            }}
            onPointerDown={(event) => {
              event.stopPropagation()
            }}
          />
        )}
      />
      <TooltipContent>
        {showHint.label}
      </TooltipContent>
    </Tooltip>
  )
}
